import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from './api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Award, CheckCircle2, XCircle, Loader2, Home } from 'lucide-react';

// Ticket 212: public page behind the link printed on every training
// certificate (no sign-in). Anyone holding the 12-character code can check
// who earned it, for which track and when.
const CODE_RE = /^[A-Za-z0-9]{6,32}$/;

const formatDate = (iso) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' });
};

function Shell({ children }) {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: '#faf7f2' }}
         data-testid="certificate-verify-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-800 mb-4">
          <Home size={14} /> BrokerForYou
        </Link>
        {children}
      </div>
    </div>
  );
}

function CodeForm({ initial = '' }) {
  const navigate = useNavigate();
  const [value, setValue] = useState(initial);
  const code = value.trim();
  const submit = (e) => {
    e.preventDefault();
    if (CODE_RE.test(code)) navigate(`/verify-certificate/${encodeURIComponent(code)}`);
  };
  return (
    <form onSubmit={submit} className="mt-5 flex gap-2" data-testid="verify-code-form">
      <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="Verification code"
             aria-label="Verification code" data-testid="verify-code-input" className="font-mono uppercase" />
      <Button type="submit" disabled={!CODE_RE.test(code)} data-testid="verify-code-submit">Check</Button>
    </form>
  );
}

export default function CertificateVerifyPage() {
  const { code } = useParams();
  const [state, setState] = useState({ status: code ? 'loading' : 'idle', cert: null });

  useEffect(() => {
    if (!code) { setState({ status: 'idle', cert: null }); return undefined; }
    let live = true;
    setState({ status: 'loading', cert: null });
    api.get(`/v2/training/certificate/verify/${encodeURIComponent(code)}`)
      .then(({ data }) => { if (live) setState({ status: 'valid', cert: data }); })
      .catch((e) => {
        if (!live) return;
        const http = e.response?.status;
        setState({ status: http === 404 ? 'missing' : http === 429 ? 'limited' : 'error', cert: null });
      });
    return () => { live = false; };
  }, [code]);

  const { status, cert } = state;

  return (
    <Shell>
      <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-6">
        {status === 'idle' && (
          <>
            <div className="flex items-center gap-2 text-slate-900 font-bold text-xl"><Award size={22} /> Check a certificate</div>
            <p className="text-slate-500 text-sm mt-1">Enter the verification code printed at the bottom of the certificate.</p>
            <CodeForm />
          </>
        )}

        {status === 'loading' && (
          <div className="flex items-center gap-2 text-slate-500" data-testid="verify-loading">
            <Loader2 size={16} className="animate-spin" /> Checking {code}…
          </div>
        )}

        {status === 'valid' && cert && (
          <div data-testid="verify-valid">
            <div className="flex items-center gap-2 text-emerald-700 font-bold text-xl">
              <CheckCircle2 size={22} /> Valid certificate
            </div>
            <div className="mt-4 text-2xl font-bold text-slate-900" data-testid="verify-name">{cert.user_name}</div>
            <div className="text-slate-600 mt-1">
              completed the {cert.track_name || 'training track'}
              {cert.tenant_name ? <> at {cert.tenant_name}</> : null}
              {cert.issued_at ? <> on {formatDate(cert.issued_at)}</> : null}.
            </div>
            <div className="mt-4 text-xs text-slate-500">
              Verification code <b className="font-mono text-slate-800">{cert.verification_code}</b>
            </div>
          </div>
        )}

        {status === 'missing' && (
          <div data-testid="verify-missing">
            <div className="flex items-center gap-2 text-rose-700 font-bold text-xl"><XCircle size={22} /> Not found</div>
            <p className="text-slate-600 text-sm mt-2">
              No certificate has the code <b className="font-mono">{code}</b>. Check the code for typos and try again.
            </p>
            <CodeForm initial={code} />
          </div>
        )}

        {status === 'limited' && (
          <div data-testid="verify-limited">
            <div className="flex items-center gap-2 text-amber-700 font-bold text-xl"><XCircle size={22} /> Too many checks</div>
            <p className="text-slate-600 text-sm mt-2">Wait a minute, then reload this page.</p>
          </div>
        )}

        {status === 'error' && (
          <div data-testid="verify-error">
            <div className="flex items-center gap-2 text-rose-700 font-bold text-xl"><XCircle size={22} /> Could not check</div>
            <p className="text-slate-600 text-sm mt-2">Something went wrong reaching the server. Reload to try again.</p>
          </div>
        )}
      </div>
    </Shell>
  );
}
